import { createContext, useContext, useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";

const GSAPContext = createContext({
  lenis: null,
  gsap,
  ScrollTrigger,
  reducedMotion: false,
});

export function useGSAPContext() {
  const ctx = useContext(GSAPContext);
  if (!ctx) {
    throw new Error("useGSAPContext must be used within GSAPProvider");
  }
  return ctx;
}

export function GSAPProvider({ children }) {
  const lenisRef = useRef(null);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mediaQuery.matches);
    const onChange = (e) => setReducedMotion(e.matches);
    mediaQuery.addEventListener("change", onChange);

    if (!mediaQuery.matches) {
      const lenis = new Lenis({
        duration: 1.1,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: "vertical",
        gestureOrientation: "vertical",
        smoothWheel: true,
        wheelMultiplier: 0.9,
        touchMultiplier: 1.5,
      });
      lenisRef.current = lenis;

      lenis.on("scroll", ScrollTrigger.update);

      gsap.ticker.add((time) => {
        lenis.raf(time * 1000);
      });
      gsap.ticker.lagSmoothing(0);

      ScrollTrigger.scrollerProxy(document.documentElement, {
        scrollTop(value) {
          if (arguments.length) {
            lenis.scrollTo(value, { immediate: true });
          }
          return window.scrollY;
        },
        getBoundingClientRect() {
          return {
            top: 0,
            left: 0,
            width: window.innerWidth,
            height: window.innerHeight,
          };
        },
        pinType: document.documentElement.style.transform ? "transform" : "fixed",
      });

      const onLoad = () => ScrollTrigger.refresh();
      window.addEventListener("load", onLoad);

      return () => {
        mediaQuery.removeEventListener("change", onChange);
        window.removeEventListener("load", onLoad);
        gsap.ticker.remove(lenis.raf);
        lenis.destroy();
        ScrollTrigger.getAll().forEach((st) => st.kill());
        lenisRef.current = null;
      };
    }

    return () => {
      mediaQuery.removeEventListener("change", onChange);
    };
  }, []);

  const value = {
    lenis: lenisRef.current,
    gsap,
    ScrollTrigger,
    reducedMotion,
  };

  return <GSAPContext.Provider value={value}>{children}</GSAPContext.Provider>;
}
